"use client";

import { useState, type FormEvent } from "react";
import {
  MARKETING_TERMS,
  MARKETING_TERMS_TITLE,
  MARKETING_TERMS_VERSION,
  marketingTermsHash,
} from "./campaignTerms";
import { Button } from "@/app/_module/components/ui/button";
import type { CampaignRequest, Workspace } from "./managedMarketingApi";
import { dollarsToCents, errorMessage, money } from "./marketingHelpers";
import s from "./MarketingWorkspace.module.css";

export default function CampaignRequestForm({
  workspace,
  pending,
  onSubmit,
  onCancel,
}: {
  workspace: Workspace;
  pending: boolean;
  onSubmit: (request: CampaignRequest) => Promise<void>;
  onCancel: () => void;
}) {
  const [templateId, setTemplateId] = useState(
    workspace.templates[0]?.id || "",
  );
  const [states, setStates] = useState<string[]>([]);
  const [error, setError] = useState("");
  const [agreed, setAgreed] = useState(false);
  const [termsOpen, setTermsOpen] = useState(false);
  const template = workspace.templates.find((item) => item.id === templateId);
  const settings = workspace.settings;
  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError("");
    const form = new FormData(event.currentTarget);
    try {
      if (!template || !states.length)
        throw new Error("Choose a campaign and at least one licensed state.");
      const requestedBudgetCents = dollarsToCents(String(form.get("budget")));
      const dailyLimitCents = dollarsToCents(String(form.get("daily")));
      if (
        requestedBudgetCents < settings.minimumBudgetCents ||
        requestedBudgetCents > settings.maximumBudgetCents
      )
        throw new Error(
          `Choose a budget between ${money(settings.minimumBudgetCents)} and ${money(settings.maximumBudgetCents)}.`,
        );
      if (
        dailyLimitCents < settings.minimumDailyLimitCents ||
        dailyLimitCents > requestedBudgetCents
      )
        throw new Error(
          `Daily limit must be at least ${money(settings.minimumDailyLimitCents)} and no more than your total budget.`,
        );
      const signatureName = String(form.get("signature") || "").trim();
      if (!agreed)
        throw new Error("Agree to the campaign advertising terms before requesting a campaign.");
      if (signatureName.length < 2)
        throw new Error("Type your full name to sign.");
      await onSubmit({
        name: String(form.get("name")).trim(),
        templateId,
        product: template.product,
        states,
        requestedBudgetCents,
        dailyLimitCents,
        notes: String(form.get("notes")).trim(),
        acceptance: {
          accepted: true,
          version: MARKETING_TERMS_VERSION,
          signatureName,
          hash: await marketingTermsHash(),
        },
      });
    } catch (err) {
      setError(errorMessage(err));
    }
  }
  return (
    <section aria-labelledby="campaign-request-heading">
      <h2
        id="campaign-request-heading"
        className="mt-6 text-2xl font-semibold tracking-tight"
      >
        Put your name in front of local families.
      </h2>
      <p className="mt-2 max-w-2xl text-sm text-muted-foreground">
        Choose the coverage and budget. Our team prepares your campaign for
        approval before launch.
      </p>
      <form className={s.form} onSubmit={submit}>
        <label className={s.label}>
          Campaign name
          <input
            name="name"
            className={s.field}
            required
            maxLength={120}
            placeholder="My final expense campaign"
          />
        </label>
        <label className={s.label}>
          Campaign
          <select
            className={s.field}
            value={templateId}
            onChange={(e) => setTemplateId(e.target.value)}
            required
          >
            {workspace.templates.map((item) => (
              <option key={item.id} value={item.id}>
                {item.name}
              </option>
            ))}
          </select>
          <span className="font-normal text-muted-foreground">
            {template?.description}
          </span>
        </label>
        <fieldset>
          <legend className="mb-3 text-sm font-medium">
            Where should your campaign run?
          </legend>
          <div className="flex flex-wrap gap-2">
            {workspace.profile?.licensedStates.map((state) => (
              <label
                key={state}
                className="flex min-h-11 cursor-pointer items-center gap-2 rounded-md border px-4 text-sm"
              >
                <input
                  type="checkbox"
                  checked={states.includes(state)}
                  onChange={(e) =>
                    setStates((current) =>
                      e.target.checked
                        ? [...current, state]
                        : current.filter((value) => value !== state),
                    )
                  }
                />
                {state}
              </label>
            ))}
          </div>
          <p className="mt-2 text-xs text-muted-foreground">
            Available states come from your Licensedproducer license records.
          </p>
        </fieldset>
        <div className={s.formGrid}>
          <label className={s.label}>
            Total ad budget (USD)
            <input
              className={s.field}
              name="budget"
              inputMode="decimal"
              placeholder="500.00"
              required
            />
            <span className="font-normal text-muted-foreground">
              Minimum {money(settings.minimumBudgetCents)}
            </span>
          </label>
          <label className={s.label}>
            Daily spending limit (USD)
            <input
              className={s.field}
              name="daily"
              inputMode="decimal"
              placeholder="25.00"
              required
            />
            <span className="font-normal text-muted-foreground">
              Our team sets this on your campaign.
            </span>
          </label>
        </div>
        <label className={s.label}>
          Anything our team should know?{" "}
          <span className="sr-only">Optional</span>
          <textarea
            name="notes"
            className={s.field}
            rows={3}
            maxLength={2000}
            placeholder="Areas you serve, your availability, or a specific focus. Optional."
          />
        </label>
        <p className="text-sm text-muted-foreground">
          Submitting a request does not charge you. You’ll review the creative
          and fund this campaign separately. Results depend on the audience and
          campaign; leads are not guaranteed.
        </p>
        {error && (
          <p role="alert" className="text-sm text-destructive">
            {error}
          </p>
        )}
        <div className="flex flex-wrap gap-3">
          <div className="mb-4 rounded-lg border p-4">
            <p className="font-medium">{MARKETING_TERMS_TITLE}</p>
            <button
              type="button"
              className="mt-1 text-sm underline underline-offset-4"
              onClick={() => setTermsOpen(!termsOpen)}
              aria-expanded={termsOpen}
            >
              {termsOpen ? "Hide the terms" : "Read the terms"}
            </button>
            {termsOpen && (
              <div className="mt-3 max-h-64 space-y-3 overflow-auto pr-2 text-sm">
                {MARKETING_TERMS.map((section) => (
                  <div key={section.heading}>
                    <p className="font-medium">{section.heading}</p>
                    <p className="text-muted-foreground">{section.body}</p>
                  </div>
                ))}
              </div>
            )}
            {/* Unchecked by default and never pre-filled: a signature that the
                page supplied for them is not an affirmative act. */}
            <label className="mt-3 flex items-start gap-2 text-sm">
              <input
                type="checkbox"
                className="mt-1"
                checked={agreed}
                onChange={(e) => setAgreed(e.target.checked)}
              />
              <span>
                I authorise Licensedproducer to advertise in my name in the states
                I selected, I hold an active licence in each of them, and I agree
                to these terms.
              </span>
            </label>
            <label className="mt-3 block text-sm">
              <span className="text-muted-foreground">Type your full name to sign</span>
              <input
                name="signature"
                className="mt-1 w-full rounded-md border px-3 py-2"
                autoComplete="off"
                placeholder="Your full legal name"
              />
            </label>
            <p className="mt-2 text-xs text-muted-foreground">
              Version {MARKETING_TERMS_VERSION}. Your name, the date, and your IP
              address are recorded with this authorisation.
            </p>
          </div>
          <Button className={s.primary} type="submit" disabled={pending || !agreed}>
            {pending ? "Sending request…" : "Request campaign"}
          </Button>
          <Button
            variant="outline"
            type="button"
            disabled={pending}
            onClick={onCancel}
          >
            Cancel
          </Button>
        </div>
      </form>
    </section>
  );
}
